import SearchIcon from "../../assets/icons/SearchIcon";
import { Children, PropsWithChildren, useEffect, useMemo, useState } from "react";

export type BaseFilters = {
  search?: string;
  limit: number;
};

const FilterToolBar = <T extends BaseFilters>({
  filters,
  className,
  children,
  onChange,
  fluid,
  layout,
  searchMaxClass
}: PropsWithChildren<{
  filters: T;
  className?: string;
  onChange: (filters: T) => void;
  fluid?: boolean;
  /**
   * toolbar: single row with search taking remaining width and filters on the right
   * stacked: default behavior (items can wrap)
   */
  layout?: "toolbar" | "stacked";
  /** Optional max-width class for the search bar (only used in toolbar layout when not fluid) */
  searchMaxClass?: string;
  }>) => {
  const [search, setSearch] = useState<string>(filters.search || "");

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      onChange({
        ...filters,
        search: search === "" ? undefined : search
      });
    }, 500); // Delay of 500ms before triggering search

    return () => clearTimeout(delayDebounceFn); // Cleanup function
  }, [search]);

  const sectionClass = useMemo(() => {
    const base = "rounded-lg bg-white px-6 py-5 shadow-sm";
    if (layout === "toolbar") {
      const childCount = Children.toArray(children).filter(Boolean).length;
      const colsClass = childCount === 2 ? "md:grid-cols-3" : "md:grid-cols-4";
      return `${base} grid grid-cols-1 ${colsClass} gap-4 items-center w-full`;
    }
    // Default stacked/flexible layout
    return base + " flex flex-wrap items-center gap-4";
  }, [layout, children]);

  return (
    <div className={"w-full " + className}>
      <section className={sectionClass}>
        {/* Search bar */}
        <div
          className={
            (layout === "toolbar"
              ? "relative w-full "
              : "relative w-full flex-grow min-w-[240px] ") +
            (fluid
              ? ""
              : layout === "toolbar"
                ? ""
                : " sm:max-w-[420px]")
          }
        >
          <input
            type="text"
            placeholder="Search"
            className="w-full rounded-lg border border-[#D1D5DB] px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#010D2C]/20"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <SearchIcon
            className="absolute right-4 top-1/2 -translate-y-1/2 stroke-ctb-blue-50 hover:cursor-pointer"
            onClick={() =>
              onChange({
                ...filters,
                search: search === "" ? undefined : search
              })
            }
          />
        </div>

        {/* Dropdowns */}
        {layout === "toolbar" ? (
          children
        ) : (
          <div className="ml-auto flex flex-wrap gap-4">{children}</div>
        )}
      </section>
    </div>
  );
};

export default FilterToolBar;
